/* global login, license, window */
import React, { createContext, useEffect, useState } from "react";
import { atEntryNode } from "common/entryNode";

const ProfileContext = createContext(null);

// The response of "show system users" carries a leading "BQN" column when the
// command is run at a specific BQN, so the columns are located by their name
// instead of by a fixed position.
const columnsOfUsers = (header) => {
  const names = header.trim().split(/\s+/);
  return { user: names.indexOf("USER"), profile: names.indexOf("PROFILE") };
};

const userAndProfile = (columns) => (row) => {
  const values = row.trim().split(/\s+/);
  return [values[columns.user], values[columns.profile]];
};

export const findUserProfile = (user) => (input) => {
  const [header, ...rows] = input.trim("\n").split("\n");
  const columns = columnsOfUsers(header);
  const [_name, profile] =
    rows
      .map(userAndProfile(columns))
      .find(([name, _profile]) => user === name) || [];
  return profile || "unknown";
};
const _PKT_ENG_DEFAULTS = {
  hideRates: false,
  dpiEnabled: false,
};

const isDPIEnabled = () => window.functionality.isEnabled("dpi-per-user");

const intoPktEngProps = (settings, row) => {
  const [field, value] = row.trim().split(/:\s?/);
  return field === "Hide policy rate details for operator"
    ? {
        ...settings,
        hideRates: ( settings.isOperator === true && value === "yes" ),
      }
    : field == "DPI-per-user for operator"
    ? {
        ...settings,
        dpiEnabled:
          isDPIEnabled() === true &&
          ( settings.isAdmin === true || value === "yes" ),
      }
    : settings;
};
const extendWithPktParamsGiven = (profile) => (input) => {
  const rows = input.trim("\n").split("\n");
  return rows.reduce(intoPktEngProps, { ..._PKT_ENG_DEFAULTS, ...profile });
};

const doRequestPktEngParams = () =>
  atEntryNode("show pkteng parameters").then((command) => ifCl.run(command));


let profileRequested = { user: null, request: null };

export const retrieveUserProfile = (user) => {
  if (profileRequested.user !== user) {
    profileRequested = {
      user,
      request: atEntryNode("show system users")
        .then((command) => ifCl.run(command))
        .then(findUserProfile(user))
        .catch((error) => {
          clearUserProfile();
          throw error;
        }),
    };
  }

  return profileRequested.request;
};

// The profile of a session that ends is not the one of the next: the user
// logging in again is asked for anew, since the BQN may have given it
// another profile in between.
export const clearUserProfile = () => {
  profileRequested = { user: null, request: null };
};


export const asProfileParams = (profile) => ({
  isOperator: profile === "operator",
  isAdmin: profile === "administrator",
});

const mergeWith = ({...source}) => ({...target}) =>
  ({...source, ...target})

// The rates of the policies are hidden from an operator when the BQN is
// configured for it, which a class of the body of the document tells every
// view. It is set where the profile is resolved, so that the parameters of
// the packet engine are asked for once and not once per reader of them.
const HIDDEN_RATES_CLASS = "profile-operator-with-hidden-rate";

const applyHideRates = (profile) => {
  const classes = document.body.classList;

  profile.hideRates === true
    ? classes.add(HIDDEN_RATES_CLASS)
    : classes.remove(HIDDEN_RATES_CLASS);

  return profile;
};

const useAcquireProfile = () => {
  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState(
    (window.login && window.login.user) || null
  );
  window.setUsername = setUsername;
  useEffect(() => {
    if (username !== null)
      retrieveUserProfile(username)
        .then(asProfileParams)
        .then((profile) =>
          doRequestPktEngParams().then(extendWithPktParamsGiven(profile))
        )
        .then(mergeWith(window.login))
        .then(applyHideRates)
        .then(setProfile);
  }, [username]);
  return profile;
};
export const ProfileContextProvider = ({ children }) => {
  const profile = useAcquireProfile();
  return (
    <ProfileContext.Provider value={profile}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileContext;
